import React, { useState, useRef, useEffect } from "react";
import SecureImage from "../../components/BucketImage/Image";
import { calendar } from "../../common/svg";
import { DatePickerProps } from "../../service/interfaces/props.interface";

const DatePicker: React.FC<DatePickerProps> = ({
  label = "Enter Date",
  name,
  onDateChange,
  defaultValue,
  register,
  errors,
  disabled = false,
  setValue,
  trigger,
  allowPastDates = false,
  includeTime = false,
  placeholder = "Select a date",
  dateFormat = "YYYY-MM-DD",
  minDate,
  disableDatesBeforeIfSpecificDateSelected
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const parseDate = (dateStr?: string): Date | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const [selectedDate, setSelectedDate] = useState<Date | null>(parseDate(defaultValue));
  const [currentDate, setCurrentDate] = useState<Date>(parseDate(defaultValue) || new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState("12");
  const [selectedMinute, setSelectedMinute] = useState("00");
  const [selectedMeridian, setSelectedMeridian] = useState("AM");
  const [showAbove, setShowAbove] = useState(false);
  const [showLeft, setShowLeft] = useState(false);
  const [viewMode, setViewMode] = useState<"days" | "months" | "years">("days");
  const [dynamicMinDate, setDynamicMinDate] = useState<Date | null>(parseDate(minDate));
  const [showTimeOnly, setShowTimeOnly] = useState(false);

  const today = new Date();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fullMonths = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  // Initialize time from default value
  useEffect(() => {
    if (defaultValue && includeTime) {
      const date = parseDate(defaultValue);
      if (date) {
        const hours = date.getHours();
        setSelectedHour((hours % 12 || 12).toString().padStart(2, "0"));
        setSelectedMinute(date.getMinutes().toString().padStart(2, "0"));
        setSelectedMeridian(hours >= 12 ? "PM" : "AM");
      }
    }
  }, [defaultValue, includeTime]);

  useEffect(() => {
    const parsed = parseDate(defaultValue);
    setSelectedDate(parsed);
    if (parsed) setCurrentDate(parsed);
  }, [defaultValue]);

  // Update dynamic min date
  // Initialize time from default value
  useEffect(() => {
    if (defaultValue && includeTime) {
      const date = parseDate(defaultValue);
      if (date) {
        const hours = date.getHours();
        setSelectedHour((hours % 12 || 12).toString().padStart(2, "0"));
        setSelectedMinute(date.getMinutes().toString().padStart(2, "0"));
        setSelectedMeridian(hours >= 12 ? "PM" : "AM");
      }
    }
  }, [defaultValue, includeTime]);

  // ✅ NEW: Ensure selectedDate updates when defaultValue changes after first render
  useEffect(() => {
    const parsed = parseDate(defaultValue);
    setSelectedDate(parsed);
    if (parsed) setCurrentDate(parsed);
  }, [defaultValue]);

  // Update dynamic min date
  useEffect(() => {
    setDynamicMinDate(parseDate(minDate));
    if (selectedDate && disableDatesBeforeIfSpecificDateSelected) {
      const specificDate = parseDate(disableDatesBeforeIfSpecificDateSelected.date);
      if (specificDate && selectedDate.toDateString() === specificDate.toDateString()) {
        const newMinDate = new Date(specificDate);
        newMinDate.setDate(specificDate.getDate() + disableDatesBeforeIfSpecificDateSelected.daysToAdd);
        setDynamicMinDate(newMinDate);
      }
    }
  }, [selectedDate, disableDatesBeforeIfSpecificDateSelected, minDate]);


  const formatDate = (date: Date, forBackend = false): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    let formatted = forBackend || dateFormat === "YYYY-MM-DD" ? `${year}-${month}-${day}` : `${day}-${month}-${year}`;

    if (includeTime) {
      if (forBackend) {
        let hours = parseInt(selectedHour);
        if (selectedMeridian === "PM" && hours < 12) hours += 12;
        else if (selectedMeridian === "AM" && hours === 12) hours = 0;
        formatted += `T${hours.toString().padStart(2, "0")}:${selectedMinute}:00`;
      } else {
        formatted += ` ${selectedHour}:${selectedMinute} ${selectedMeridian}`;
      }
    }
    return formatted;
  };

  const updateDate = (date: Date) => {
    const formatted = formatDate(date, true);
    onDateChange(formatted);
    if (setValue) {
      setValue(name, formatted);
      trigger?.(name);
    }
  };

  const isDateDisabled = (date: Date): boolean => {
    if (dynamicMinDate) {
      return date < new Date(dynamicMinDate.getFullYear(), dynamicMinDate.getMonth(), dynamicMinDate.getDate());
    }
    if (!allowPastDates) {
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return date < todayStart;
    }
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    let newDate = new Date(date);
    if (includeTime) {
      let hours = parseInt(selectedHour);
      if (selectedMeridian === "PM" && hours < 12) hours += 12;
      else if (selectedMeridian === "AM" && hours === 12) hours = 0;
      newDate.setHours(hours, parseInt(selectedMinute));
    }

    setSelectedDate(newDate);
    updateDate(newDate);

    if (includeTime) {
      setShowTimeOnly(true);
    } else {
      setIsCalendarOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDate(null);
    onDateChange("");
    setShowTimeOnly(false);
    if (setValue) {
      setValue(name, "");
      trigger?.(name);
    }
  };

  const calculatePosition = () => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const calendarWidth = 288; // Calendar width (w-72 = 288px)
    const calendarHeight = 400; // Approximate calendar height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate position for fixed positioning
    let top = rect.bottom + 8; // 8px gap below input
    const left = rect.left;
    const right = viewportWidth - rect.right;

    // Check if calendar would be cut off by bottom of viewport
    // Add extra margin for taskbar (80px) and some padding (20px)
    const taskbarMargin = 100; // Extra margin for taskbar
    const spaceBelow = viewportHeight - rect.bottom - taskbarMargin;
    const spaceAbove = rect.top;

    // If not enough space below, show above the input
    if (spaceBelow < calendarHeight + 20 && spaceAbove > calendarHeight + 20) {
      top = rect.top - calendarHeight - 8; // 8px gap above input
      setShowAbove(true);
    } else {
      setShowAbove(false);
    }

    // Check horizontal positioning
    const spaceRight = viewportWidth - rect.left;
    const shouldShowLeft = spaceRight < calendarWidth && rect.right > calendarWidth;

    setShowLeft(shouldShowLeft);

    // Store position for fixed positioning
    if (inputRef.current) {
      inputRef.current.dataset.calendarTop = top.toString();
      inputRef.current.dataset.calendarLeft = left.toString();
      inputRef.current.dataset.calendarRight = right.toString();
    }
  };

  const toggleCalendar = () => {
    if (disabled) return;
    if (!isCalendarOpen) {
      calculatePosition();
    }
    setIsCalendarOpen(!isCalendarOpen);
    setShowTimeOnly(false);
    setViewMode("days");
  };

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
        setShowTimeOnly(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isCalendarOpen) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCalendarOpen]);

  // Update time changes
  useEffect(() => {
    if (selectedDate && includeTime) {
      let hours = parseInt(selectedHour);
      if (selectedMeridian === "PM" && hours < 12) hours += 12;
      else if (selectedMeridian === "AM" && hours === 12) hours = 0;

      const newDate = new Date(selectedDate);
      newDate.setHours(hours, parseInt(selectedMinute));
      setSelectedDate(newDate);
      updateDate(newDate);
    }
  }, [selectedHour, selectedMinute, selectedMeridian]);

  const generateCalendarDays = () => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const days: (Date | null)[] = [];

    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
    }
    return days;
  };

  const NavButton = ({ onClick, children, className = "" }: { onClick: () => void; children: React.ReactNode; className?: string }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center w-10 h-10 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-200 ${className}`}
    >
      {children}
    </button>
  );

  const ArrowLeft = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const ArrowRight = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const renderDaysView = () => (
    <div className="animate-in slide-in-from-left-2 duration-200">
      <div className="flex justify-between items-center mb-4 px-2">
        <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>
          <ArrowLeft />
        </NavButton>
        <button
          type="button"
          className="font-semibold text-lg text-gray-800 hover:text-blue-600 hover:bg-blue-50 py-2 px-4 rounded-lg transition-all duration-200"
          onClick={() => setViewMode("months")}
        >
          {fullMonths[currentDate.getMonth()]} {currentDate.getFullYear()}
        </button>
        <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>
          <ArrowRight />
        </NavButton>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {generateCalendarDays().map((date, i) => {
          if (!date) return <div key={i} className="h-12" />;

          const isToday = date.toDateString() === today.toDateString();
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
          const isDisabled = isDateDisabled(date);

          return (
            <div key={i}
              className={`h-10 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-all duration-200 transform hover:scale-105 ${isSelected
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                : isDisabled
                  ? "text-gray-300 cursor-not-allowed bg-gray-50"
                  : isToday
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200 border-2 border-blue-300"
                    : "hover:bg-blue-50 text-gray-700 hover:text-blue-600"
                }`}
              onClick={() => !isDisabled && handleDateClick(date)}>
              {date.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMonthsView = () => (
    <div className="animate-in slide-in-from-right-2 duration-200">
      <div className="flex justify-between items-center mb-6 px-2">
        <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1))}>
          <ArrowLeft />
        </NavButton>
        <button
          type="button"
          className="font-semibold text-lg text-gray-800 hover:text-blue-600 hover:bg-blue-50 py-2 px-4 rounded-lg transition-all duration-200"
          onClick={() => setViewMode("years")}
        >
          {currentDate.getFullYear()}
        </button>
        <NavButton onClick={() => setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1))}>
          <ArrowRight />
        </NavButton>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {months.map((month, index) => {
          const isSelected = selectedDate?.getMonth() === index && selectedDate?.getFullYear() === currentDate.getFullYear();
          return (
            <div key={month}
              className={`h-14 flex items-center justify-center rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 transform hover:scale-105 ${isSelected
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                : "hover:bg-blue-50 text-gray-700 hover:text-blue-600"
                }`}
              onClick={() => {
                setCurrentDate(new Date(currentDate.getFullYear(), index, 1));
                setViewMode("days");
              }}>
              {month}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderYearsView = () => {
    const startYear = Math.floor(currentDate.getFullYear() / 10) * 10;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i - 1);

    return (
      <div className="animate-in slide-in-from-right-2 duration-200">
        <div className="flex justify-between items-center mb-6 px-2">
          <NavButton onClick={() => setCurrentDate(new Date(startYear - 12, 0, 1))}>
            <ArrowLeft />
          </NavButton>
          <span className="font-semibold text-lg text-gray-800">
            {years[0]} - {years[11]}
          </span>
          <NavButton onClick={() => setCurrentDate(new Date(startYear + 12, 0, 1))}>
            <ArrowRight />
          </NavButton>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {years.map(year => {
            const isSelected = year === selectedDate?.getFullYear();
            return (
              <div key={year}
                className={`h-14 flex items-center justify-center rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 transform hover:scale-105 ${isSelected
                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                  : "hover:bg-blue-50 text-gray-700 hover:text-blue-600"
                  }`}
                onClick={() => {
                  setCurrentDate(new Date(year, currentDate.getMonth(), 1));
                  setViewMode("months");
                }}>
                {year}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TimeSelect = ({ value, onChange, options, label }: {
    value: string;
    onChange: (v: string) => void;
    options: string[];
    label: string;
  }) => (
    <div className="flex flex-col items-center">
      <label className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-16 h-12 text-center border-2 border-gray-200 rounded-xl bg-white text-gray-700 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
        >
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center text-gray-400">
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </div>
  );

  const renderTimeSelector = () => {
    if (!includeTime) return null;

    const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));
    const minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

    return (
      <div className="animate-in slide-in-from-right-2 duration-300">
        <div className="flex justify-between items-center mb-6">
          <button
            type="button"
            onClick={() => setShowTimeOnly(false)}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200 group"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-blue-50 transition-colors duration-200">
              <ArrowLeft />
            </div>
            <span className="ml-2 font-medium">Back</span>
          </button>
          <div className="text-center">
            <div className="text-sm text-gray-500 mb-1">Selected Date</div>
            <div className="font-semibold text-gray-800">
              {selectedDate && formatDate(selectedDate).split(' ')[0]}
            </div>
          </div>
        </div>

        <div className="text-center mb-8">
          <div className="text-lg font-semibold text-gray-800 mb-2">Select Time</div>
          <div className="w-12 h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mx-auto"></div>
        </div>

        <div className="flex justify-center items-center gap-6 mb-8">
          <TimeSelect value={selectedHour} onChange={setSelectedHour} options={hours} label="Hour" />
          <div className="text-2xl font-bold text-gray-400 mt-6">:</div>
          <TimeSelect value={selectedMinute} onChange={setSelectedMinute} options={minutes} label="Min" />
          <TimeSelect value={selectedMeridian} onChange={setSelectedMeridian} options={["AM", "PM"]} label="Period" />
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={() => setIsCalendarOpen(false)}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg shadow-blue-500/30"
          >
            Confirm Time
          </button>
        </div>
      </div>
    );
  };

  const getCalendarPositionClasses = () => {
    if (!inputRef.current) return "";

    const top = inputRef.current.dataset.calendarTop || "0";
    const left = inputRef.current.dataset.calendarLeft || "0";
    const right = inputRef.current.dataset.calendarRight || "0";

    let classes = "fixed bg-white shadow-2xl border border-gray-200 rounded-xl z-[9999] p-4 w-72 animate-in slide-in-from-top-2 duration-300 ";

    // Use calculated position
    classes += `top-[${top}px] `;

    // Horizontal positioning
    if (showLeft) {
      classes += `right-[${right}px] `;
    } else {
      classes += `left-[${left}px] `;
    }

    // Ensure it stays within viewport bounds
    classes += "max-w-[calc(100vw-1rem)] ";

    return classes;
  };

  return (
    <div className="relative w-full" ref={calendarRef}>
      <label htmlFor={name} className="input-label text-xs font-medium text-gray-700 mb-1">
        {label || <>&nbsp;</>}
      </label>

      <div className="relative">
        <div
          ref={inputRef}
          className={`group flex items-center justify-between border rounded-md px-3 py-2 w-full transition-all duration-200 ${disabled
            ? 'bg-gray-50 cursor-not-allowed border-gray-200'
            : 'bg-white cursor-pointer hover:border-blue-400 hover:shadow-md'
            } ${errors?.[name]
              ? 'border-red-400 shadow-red-100'
              : isCalendarOpen
                ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                : 'border-gray-300'
            }`}
          onClick={toggleCalendar}
        >

          <input type="hidden" {...register} value={selectedDate ? formatDate(selectedDate, true) : ""} />
          <input
            className="flex-grow text-xs bg-transparent border-none focus:outline-none placeholder-gray-400 font-medium"
            placeholder={placeholder}
            value={selectedDate ? formatDate(selectedDate) : ""}
            readOnly
            disabled={disabled}
          />

          <div className="flex gap-3 items-center">
            {selectedDate && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-red-500 transition-colors duration-200 p-1 rounded-full hover:bg-red-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div className={`transition-colors duration-200 ${isCalendarOpen ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-500'}`}>
              <SecureImage fileKey={calendar} alt="Calendar" width="16px" />
            </div>
          </div>
        </div>

        {errors?.[name] && (
          <p className="mt-1 text-xs text-red-600 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {errors[name].message}
          </p>
        )}
      </div>

      {isCalendarOpen && !disabled && (
        <div
          className={`fixed bg-white shadow-2xl border border-gray-200 rounded-xl z-[9999] p-4 w-72 max-w-[calc(100vw-1rem)] ${showAbove
            ? 'animate-in slide-in-from-bottom-2 duration-300'
            : 'animate-in slide-in-from-top-2 duration-300'
            }`}
          style={{
            top: inputRef.current?.dataset.calendarTop ? `${inputRef.current.dataset.calendarTop}px` : '0px',
            left: showLeft ? 'auto' : (inputRef.current?.dataset.calendarLeft ? `${inputRef.current.dataset.calendarLeft}px` : '0px'),
            right: showLeft ? (inputRef.current?.dataset.calendarRight ? `${inputRef.current.dataset.calendarRight}px` : '0px') : 'auto'
          }}
        >
          {showTimeOnly ? (
            renderTimeSelector()
          ) : (
            <>
              {viewMode === "days" && renderDaysView()}
              {viewMode === "months" && renderMonthsView()}
              {viewMode === "years" && renderYearsView()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;